import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Typography, Paper } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ArrowDownward as ArrowDownIcon, ArrowBack as ArrowBackIcon } from '@material-ui/icons'
import strings from 'strings'
import {
  ACCENT_ORANGE,
  ACCENT_PURPLE,
  BANNER_BG,
  deriveComparisonVerdict,
  formatCurrency,
  formatMultiple,
  formatPercent,
  formatPeriodLabel,
  formatPeriodLabelLong,
  formatSignedCurrency,
  formatSignedPercent,
  getStatusStyle,
  getHealthStatusLabel,
  readMetrics,
  splitFlag
} from './balanceSheetAdapters'

const CHANGE_METRICS = [
  { key: 'totalAssetsChange', labelKey: 'BALANCE_SHEET_TOTAL_ASSETS', read: (m) => m.totalAssets, format: formatCurrency, color: BANNER_BG },
  { key: 'totalLiabilitiesChange', labelKey: 'BALANCE_SHEET_TOTAL_LIABILITIES', read: (m) => m.totalLiabilities, format: formatCurrency, color: ACCENT_ORANGE },
  { key: 'totalDebtChange', labelKey: 'BALANCE_SHEET_TOTAL_DEBT', read: (m) => m.totalDebt, format: formatCurrency, color: ACCENT_ORANGE },
  { key: 'totalEquityChange', labelKey: 'BALANCE_SHEET_TOTAL_EQUITY', read: (m) => m.totalEquity, format: formatCurrency, color: ACCENT_PURPLE },
  { key: 'totalARChange', labelKey: 'BALANCE_SHEET_TOTAL_AR', read: (m) => m.totalAR, format: formatCurrency, color: ACCENT_PURPLE },
  { key: 'debtToEquityRatioChange', labelKey: 'BALANCE_SHEET_DEBT_TO_EQUITY', read: (m) => m.debtToEquityRatio, format: formatMultiple, color: ACCENT_ORANGE }
]

const ANALYSIS_TABS = [
  { key: 'positionAnalysis', labelKey: 'BALANCE_SHEET_POSITION_ANALYSIS_TAB' },
  { key: 'overallHealthAnalysis', labelKey: 'BALANCE_SHEET_OVERALL_HEALTH_TAB' },
  { key: 'takeawayAnalysis', labelKey: 'BALANCE_SHEET_TAKEAWAY_ANALYSIS_TAB' }
]

const useStyles = makeStyles((theme) => ({
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2)
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    color: '#555'
  },
  generatedLabel: {
    fontSize: 13,
    color: 'var(--neutral-400)'
  },
  banner: {
    backgroundColor: BANNER_BG,
    borderRadius: '14px',
    padding: theme.spacing(4),
    color: '#fff',
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column'
    }
  },
  bannerEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: ACCENT_PURPLE,
    marginBottom: theme.spacing(1)
  },
  bannerHeadline: {
    fontSize: 28,
    fontWeight: 700,
    marginBottom: theme.spacing(1.5)
  },
  bannerDescription: {
    fontSize: 14,
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.78)',
    maxWidth: 560
  },
  scorePairBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: '10px',
    padding: theme.spacing(2),
    alignSelf: 'flex-start'
  },
  scoreBoxLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.06em',
    color: 'rgba(255,255,255,0.6)'
  },
  scoreBoxValue: {
    fontSize: 26,
    fontWeight: 700
  },
  flagsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  flagCard: {
    flex: '1 1 260px',
    minWidth: 240,
    padding: theme.spacing(2),
    borderRadius: '12px',
    border: '1px solid #E8E8E8',
    boxShadow: 'none'
  },
  flagEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.06em',
    color: ACCENT_ORANGE,
    marginBottom: 6
  },
  flagTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 4
  },
  flagBody: {
    fontSize: 12,
    color: '#666',
    lineHeight: 1.5
  },
  flagBar: {
    height: 3,
    borderRadius: 2,
    backgroundColor: ACCENT_ORANGE,
    marginTop: theme.spacing(2)
  },
  deltaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  deltaCard: {
    flex: '1 1 180px',
    minWidth: 170,
    padding: theme.spacing(2),
    borderRadius: '12px',
    border: '1px solid #E8E8E8',
    boxShadow: 'none'
  },
  deltaLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: 'var(--neutral-400)'
  },
  deltaValue: {
    fontSize: 20,
    fontWeight: 700,
    margin: '6px 0 4px'
  },
  deltaSubtext: {
    fontSize: 11,
    color: '#777'
  },
  deltaBarTrack: {
    height: 3,
    backgroundColor: '#EFEFEF',
    borderRadius: 2,
    marginTop: theme.spacing(1.5),
    overflow: 'hidden'
  },
  deltaBarFill: {
    height: '100%',
    borderRadius: 2
  },
  sectionCard: {
    padding: theme.spacing(3),
    borderRadius: '12px',
    border: '1px solid #E8E8E8',
    boxShadow: 'none'
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: 700
  },
  sectionSubtitle: {
    fontSize: 12,
    color: 'var(--neutral-400)',
    marginTop: 2
  },
  legend: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    color: '#777'
  },
  legendSwatch: {
    width: 8,
    height: 8,
    borderRadius: 2
  },
  popRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    marginTop: theme.spacing(3)
  },
  popGroup: {
    flex: '1 1 180px',
    minWidth: 160
  },
  popPlot: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    height: 130
  },
  popBarSlot: {
    flex: 1,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end'
  },
  popValue: {
    fontSize: 10,
    color: '#777',
    marginBottom: 4
  },
  popBar: {
    borderRadius: '3px 3px 0 0',
    minHeight: 3
  },
  popMetricLabel: {
    fontSize: 11,
    color: 'var(--neutral-400)',
    marginTop: theme.spacing(1)
  },
  shiftRow: {
    marginTop: theme.spacing(2.5)
  },
  shiftHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
    marginBottom: 6
  },
  shiftTrack: {
    height: 6,
    backgroundColor: '#F3F3F3',
    borderRadius: 3,
    marginBottom: 3,
    overflow: 'hidden'
  },
  shiftFill: {
    height: '100%',
    borderRadius: 3
  },
  tabsRow: {
    display: 'flex',
    gap: theme.spacing(3),
    borderBottom: '1px solid #EEE',
    marginBottom: theme.spacing(2)
  },
  tabItem: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--neutral-400)',
    paddingBottom: theme.spacing(1),
    cursor: 'pointer'
  },
  tabItemActive: {
    color: '#222',
    borderBottom: '2px solid #222'
  },
  analysisRow: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: `${theme.spacing(1.5)}px 0`,
    borderBottom: '1px solid #F0F0F0'
  },
  analysisIndex: {
    fontSize: 11,
    fontWeight: 700,
    color: '#c4c4c4',
    marginTop: 2
  },
  analysisText: {
    fontSize: 13,
    lineHeight: 1.6
  }
}))

const changeOf = (report, key, fromValue, toValue) => {
  const reported = report[key]
  if (reported) {
    return {
      absolute: Number(reported.absolute_delta) || 0,
      percent: Number(reported.percentage_change) || 0
    }
  }
  const absolute = toValue - fromValue
  return {
    absolute,
    percent: fromValue ? (absolute / Math.abs(fromValue)) * 100 : 0
  }
}

const BalanceSheetComparison = ({ fromDoc, toDoc, comparisonData, onBack }) => {
  const classes = useStyles()
  const report = comparisonData ? comparisonData.report : null
  const availableTabs = ANALYSIS_TABS.filter((tab) => report && (report[tab.key] || []).length > 0)
  const [activeTab, setActiveTab] = useState(null)

  if (!report) {
    return (
      <Paper className={classes.sectionCard}>
        <div className={classes.topBar}>
          <div className={classes.backLink} onClick={onBack}>
            <ArrowBackIcon fontSize="small" /> {strings.BALANCE_SHEET_BACK}
          </div>
        </div>
        <Typography>{strings.BALANCE_SHEET_NO_COMPARISON_DATA}</Typography>
      </Paper>
    )
  }

  const fromMetrics = readMetrics(fromDoc)
  const toMetrics = readMetrics(toDoc)
  const fromLabel = formatPeriodLabel(comparisonData.date1 || fromMetrics.date)
  const toLabel = formatPeriodLabel(comparisonData.date2 || toMetrics.date)
  const verdict = deriveComparisonVerdict(report, fromMetrics, toMetrics)
  const selectedTab = availableTabs.some((tab) => tab.key === activeTab)
    ? activeTab
    : (availableTabs[0] && availableTabs[0].key)

  const triggeredFlags = report.triggeredFlags || []

  const deltas = CHANGE_METRICS.map((metric) => {
    const fromValue = metric.read(fromMetrics)
    const toValue = metric.read(toMetrics)
    return {
      ...metric,
      fromValue,
      toValue,
      change: changeOf(report, metric.key, fromValue, toValue)
    }
  })

  const periodMetrics = [
    { label: strings.BALANCE_SHEET_TOTAL_ASSETS, from: fromMetrics.totalAssets, to: toMetrics.totalAssets },
    { label: strings.BALANCE_SHEET_TOTAL_LIABILITIES, from: fromMetrics.totalLiabilities, to: toMetrics.totalLiabilities },
    { label: strings.BALANCE_SHEET_TOTAL_EQUITY, from: fromMetrics.totalEquity, to: toMetrics.totalEquity },
    { label: strings.BALANCE_SHEET_TOTAL_AR, from: fromMetrics.totalAR, to: toMetrics.totalAR }
  ].map((metric) => {
    const scale = Math.max(Math.abs(metric.from), Math.abs(metric.to), 1)
    return {
      ...metric,
      fromPct: (Math.abs(metric.from) / scale) * 100,
      toPct: (Math.abs(metric.to) / scale) * 100
    }
  })

  const shareOf = (value, total) => (total ? (value / total) * 100 : 0)
  const shiftRows = [
    {
      label: strings.BALANCE_SHEET_ACCOUNTS_RECEIVABLE,
      color: BANNER_BG,
      from: shareOf(fromMetrics.totalAR, fromMetrics.totalAssets),
      to: shareOf(toMetrics.totalAR, toMetrics.totalAssets)
    },
    {
      label: strings.BALANCE_SHEET_OTHER_ASSETS,
      color: '#c9ced1',
      from: shareOf(fromMetrics.otherAssets, fromMetrics.totalAssets),
      to: shareOf(toMetrics.otherAssets, toMetrics.totalAssets)
    },
    {
      label: strings.BALANCE_SHEET_TOTAL_LIABILITIES_LABEL,
      color: ACCENT_ORANGE,
      from: shareOf(fromMetrics.totalLiabilities, fromMetrics.claimsTotal),
      to: shareOf(toMetrics.totalLiabilities, toMetrics.claimsTotal)
    },
    {
      label: strings.BALANCE_SHEET_TOTAL_EQUITY_LABEL,
      color: ACCENT_PURPLE,
      from: shareOf(fromMetrics.totalEquity, fromMetrics.claimsTotal),
      to: shareOf(toMetrics.totalEquity, toMetrics.claimsTotal)
    }
  ]

  const fromStatusStyle = getStatusStyle(getHealthStatusLabel(fromMetrics.healthScore))
  const toStatusStyle = getStatusStyle(getHealthStatusLabel(toMetrics.healthScore))

  return (
    <div>
      <div className={classes.topBar}>
        <div className={classes.backLink} onClick={onBack}>
          <ArrowBackIcon fontSize="small" /> {strings.BALANCE_SHEET_BACK}
        </div>
        {report.comparisonDate && (
          <Typography className={classes.generatedLabel}>
            {strings.BALANCE_SHEET_COMPARISON_GENERATED_PREFIX} {formatPeriodLabel(report.comparisonDate)}
          </Typography>
        )}
      </div>

      <div className={classes.banner}>
        <div>
          <Typography className={classes.bannerEyebrow}>
            {formatPeriodLabelLong(fromMetrics.date).toUpperCase()} → {formatPeriodLabelLong(toMetrics.date).toUpperCase()}
          </Typography>
          <Typography className={classes.bannerHeadline}>{verdict.headline}</Typography>
          <Typography className={classes.bannerDescription}>{verdict.description}</Typography>
        </div>
        <div className={classes.scorePairBox}>
          <div>
            <Typography className={classes.scoreBoxLabel}>{fromLabel}</Typography>
            <Typography className={classes.scoreBoxValue} style={{ color: fromStatusStyle.accent }}>
              {fromMetrics.healthScore.toFixed(1)}
            </Typography>
          </div>
          <ArrowDownIcon
            style={{
              color: verdict.improved ? '#4caf82' : ACCENT_ORANGE,
              transform: verdict.improved ? 'rotate(180deg)' : 'none'
            }}
          />
          <div>
            <Typography className={classes.scoreBoxLabel}>{toLabel}</Typography>
            <Typography className={classes.scoreBoxValue} style={{ color: toStatusStyle.accent }}>
              {toMetrics.healthScore.toFixed(1)}
            </Typography>
          </div>
        </div>
      </div>

      {triggeredFlags.length > 0 && (
        <div className={classes.flagsRow}>
          {triggeredFlags.map((flag, index) => {
            const { title, body } = splitFlag(flag)
            return (
              <Paper className={classes.flagCard} key={flag}>
                <Typography className={classes.flagEyebrow}>
                  {strings.BALANCE_SHEET_TRIGGERED_FLAGS.toUpperCase()} · {String(index + 1).padStart(2, '0')}
                </Typography>
                <Typography className={classes.flagTitle}>{title}</Typography>
                <Typography className={classes.flagBody}>{body}</Typography>
                <div className={classes.flagBar} />
              </Paper>
            )
          })}
        </div>
      )}

      <div className={classes.deltaRow}>
        {deltas.map((delta) => (
          <Paper className={classes.deltaCard} key={delta.key}>
            <Typography className={classes.deltaLabel}>{strings[delta.labelKey]}</Typography>
            <Typography
              className={classes.deltaValue}
              style={{ color: delta.change.percent === 0 ? '#555' : (delta.change.percent > 0 ? 'var(--status-approved)' : ACCENT_ORANGE) }}
            >
              {delta.change.percent === 0 ? strings.BALANCE_SHEET_UNCHANGED : formatSignedPercent(delta.change.percent)}
            </Typography>
            <Typography className={classes.deltaSubtext}>
              {delta.format(delta.fromValue)} → {delta.format(delta.toValue)}
            </Typography>
            {delta.change.absolute !== 0 && (
              <Typography className={classes.deltaSubtext}>
                {delta.key === 'debtToEquityRatioChange'
                  ? `${delta.change.absolute >= 0 ? '+' : ''}${delta.change.absolute.toFixed(2)}`
                  : formatSignedCurrency(delta.change.absolute)}
              </Typography>
            )}
            <div className={classes.deltaBarTrack}>
              <div
                className={classes.deltaBarFill}
                style={{
                  width: `${Math.max(2, Math.min(100, Math.abs(delta.change.percent)))}%`,
                  backgroundColor: delta.color
                }}
              />
            </div>
          </Paper>
        ))}
      </div>

      <Paper className={classes.sectionCard} style={{ marginBottom: 24 }}>
        <Typography className={classes.sectionTitle}>{strings.BALANCE_SHEET_PERIOD_OVER_PERIOD}</Typography>
        <Typography className={classes.sectionSubtitle}>{strings.BALANCE_SHEET_PERIOD_OVER_PERIOD_SUBTITLE}</Typography>

        <div className={classes.legend}>
          <span className={classes.legendItem}>
            <span className={classes.legendSwatch} style={{ backgroundColor: BANNER_BG }} />
            {strings.BALANCE_SHEET_PREVIOUS} · {fromLabel}
          </span>
          <span className={classes.legendItem}>
            <span className={classes.legendSwatch} style={{ backgroundColor: ACCENT_PURPLE }} />
            {strings.BALANCE_SHEET_CURRENT} · {toLabel}
          </span>
        </div>

        <div className={classes.popRow}>
          {periodMetrics.map((metric) => (
            <div className={classes.popGroup} key={metric.label}>
              <div className={classes.popPlot}>
                <div className={classes.popBarSlot}>
                  <Typography className={classes.popValue}>{formatCurrency(metric.from)}</Typography>
                  <div className={classes.popBar} style={{ height: `${metric.fromPct}%`, backgroundColor: BANNER_BG }} />
                </div>
                <div className={classes.popBarSlot}>
                  <Typography className={classes.popValue}>{formatCurrency(metric.to)}</Typography>
                  <div className={classes.popBar} style={{ height: `${metric.toPct}%`, backgroundColor: ACCENT_PURPLE }} />
                </div>
              </div>
              <Typography className={classes.popMetricLabel}>{metric.label}</Typography>
            </div>
          ))}
        </div>
      </Paper>

      <Paper className={classes.sectionCard} style={{ marginBottom: 24 }}>
        <Typography className={classes.sectionTitle}>{strings.BALANCE_SHEET_WHERE_SHIFTED}</Typography>
        <Typography className={classes.sectionSubtitle}>
          {strings.BALANCE_SHEET_WHERE_SHIFTED_SUBTITLE}, {fromLabel} → {toLabel}
        </Typography>
        {shiftRows.map((row) => (
          <div className={classes.shiftRow} key={row.label}>
            <div className={classes.shiftHeader}>
              <span>{row.label}</span>
              <span style={{ color: 'var(--neutral-400)' }}>{formatPercent(row.from)} → {formatPercent(row.to)}</span>
            </div>
            <div className={classes.shiftTrack}>
              <div
                className={classes.shiftFill}
                style={{ width: `${Math.max(0, Math.min(100, row.from))}%`, backgroundColor: row.color, opacity: 0.45 }}
              />
            </div>
            <div className={classes.shiftTrack}>
              <div
                className={classes.shiftFill}
                style={{ width: `${Math.max(0, Math.min(100, row.to))}%`, backgroundColor: row.color }}
              />
            </div>
          </div>
        ))}
      </Paper>

      <Paper className={classes.sectionCard}>
        {availableTabs.length > 0 ? (
          <>
            <div className={classes.tabsRow}>
              {availableTabs.map((tab) => (
                <div
                  key={tab.key}
                  className={`${classes.tabItem} ${selectedTab === tab.key ? classes.tabItemActive : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {strings[tab.labelKey]}
                </div>
              ))}
            </div>
            {(report[selectedTab] || []).map((line, index) => (
              <div className={classes.analysisRow} key={line}>
                <Typography className={classes.analysisIndex}>{String(index + 1).padStart(2, '0')}</Typography>
                <Typography className={classes.analysisText}>{line}</Typography>
              </div>
            ))}
          </>
        ) : (
          <Typography>{strings.BALANCE_SHEET_NO_COMPARISON_DATA}</Typography>
        )}
      </Paper>
    </div>
  )
}

BalanceSheetComparison.propTypes = {
  fromDoc: PropTypes.object.isRequired,
  toDoc: PropTypes.object.isRequired,
  comparisonData: PropTypes.object,
  onBack: PropTypes.func.isRequired
}

export default BalanceSheetComparison
